import React, { useMemo, useState } from "react";
import { Box, Paper, Skeleton, TableContainer, Typography, Popover } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getTicket } from "../../services/ticket.service";

const TicketChannelTable = ({ ticketChannelData, loading = false }) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState("closedWithout"); // openWithout | closedWithout | totalWithout | openWithin | closedWithin
  const [sortAsc, setSortAsc] = useState(false);

  const sortedData = useMemo(() => {
    const copy = Array.isArray(ticketChannelData) ? [...ticketChannelData] : [];
    return copy.sort((a, b) => {
      const openWithoutA = a?.open?.without || 0;
      const closedWithoutA = a?.closed?.without || 0;
      const openWithoutB = b?.open?.without || 0;
      const closedWithoutB = b?.closed?.without || 0;
      const openWithinA = a?.open?.within || 0;
      const closedWithinA = a?.closed?.within || 0;
      const openWithinB = b?.open?.within || 0;
      const closedWithinB = b?.closed?.within || 0;
      const totalWithoutA = openWithoutA + closedWithoutA;
      const totalWithoutB = openWithoutB + closedWithoutB;

      let va = 0;
      let vb = 0;
      if (sortKey === "openWithout") { va = openWithoutA; vb = openWithoutB; }
      else if (sortKey === "closedWithout") { va = closedWithoutA; vb = closedWithoutB; }
      else if (sortKey === "openWithin") { va = openWithinA; vb = openWithinB; }
      else if (sortKey === "closedWithin") { va = closedWithinA; vb = closedWithinB; }
      else { va = totalWithoutA; vb = totalWithoutB; }
      return sortAsc ? va - vb : vb - va;
    });
  }, [ticketChannelData, sortKey, sortAsc]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    if (ticket && ticket._id) {
      try {
        // Fetch full ticket data using getTicket API
        // Search by ticketId for exact match
        const ticketIdToSearch = ticket.ticketId || ticket._id;
        const response = await getTicket({
          search: ticketIdToSearch,
          limit: 100, // Increase limit to ensure we find the ticket
        });
        
        // Find exact match by ticketId or _id
        let fullTicket = null;
        if (response?.data && response.data.length > 0) {
          // First try exact ticketId match
          fullTicket = response.data.find(
            (t) => t.ticketId === ticketIdToSearch
          );
          
          // If not found, try _id match (handle both string and ObjectId)
          if (!fullTicket) {
            fullTicket = response.data.find(
              (t) => {
                const tId = t._id?.toString();
                const ticketId = ticket._id?.toString();
                return tId === ticketId;
              }
            );
          }
          
          // Fallback to first result if exact match not found
          if (!fullTicket && response.data.length > 0) {
            fullTicket = response.data[0];
          }
        }
        
        if (fullTicket) {
          // Remove timer-related fields before navigation
          const {
            assignTimer,
            finalTimer,
            followUpTimer,
            initialTimer,
            timer,
            ...cleanTicket
          } = fullTicket;
          console.log("Navigating with full ticket:", cleanTicket);
          navigate("/feedback", { state: cleanTicket });
        } else {
          console.warn("Full ticket not found, using minimal data");
          // Fallback: navigate with minimal ticket data if fetch fails
          const {
            assignTimer,
            finalTimer,
            followUpTimer,
            initialTimer,
            timer,
            ...cleanTicket
          } = ticket;
          navigate("/feedback", { state: cleanTicket });
        }
      } catch (error) {
        console.error("Error fetching ticket:", error);
        // Fallback: navigate with minimal ticket data if fetch fails
        const {
          assignTimer,
          finalTimer,
          followUpTimer,
          initialTimer,
          timer,
          ...cleanTicket
        } = ticket;
        navigate("/feedback", { state: cleanTicket });
      }
    }
  };

  const TicketCountCell = ({ count, tickets, type, isLast = false }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handlePopoverOpen = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    const handleTicketClickInCell = (ticket) => {
      handlePopoverClose();
      if (ticket && ticket._id) {
        // Remove timer-related fields before navigation
        const {
          assignTimer,
          finalTimer,
          followUpTimer,
          initialTimer,
          timer,
          ...cleanTicket
        } = ticket;
        // Ensure _id is properly formatted for navigation
        const ticketForNavigation = {
          ...cleanTicket,
          _id: ticket._id.toString ? ticket._id.toString() : ticket._id
        };
        console.log("Navigating with ticket:", ticketForNavigation);
        navigate("/feedback", { state: ticketForNavigation });
      }
    };

    if (!count || count === 0) {
      return (
        <td style={{
          fontFamily: "Inter", 
          borderRight: isLast ? "none" : "1px solid #f3f4f6", 
          padding: "10px 12px", 
          textAlign: "center", 
          color: "#6b7280" 
        }}>
          {count || 0}
        </td>
      );
    }

    return (
      <td style={{
        fontFamily: "Inter", 
        borderRight: isLast ? "none" : "1px solid #f3f4f6", 
        padding: "10px 12px", 
        textAlign: "center", 
        color: type === "closed" ? (count > 0 ? "#16a34a" : "#6b7280") : "#6b7280",
        cursor: "pointer",
        position: "relative"
      }}>
        <Box
          aria-owns={open ? "ticket-popover" : undefined}
          aria-haspopup="true"
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          sx={{
            display: "inline-block",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
              color: "#00675d"
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Navigate to first ticket if available
            if (tickets && tickets.length > 0) {
              handleTicketClickInCell(tickets[0]);
            }
          }}
        >
          {count}
        </Box>
        <Popover
          id="ticket-popover"
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          sx={{
            pointerEvents: "none",
            "& .MuiPopover-paper": {
              pointerEvents: "auto",
            },
          }}
          onMouseEnter={() => setAnchorEl(anchorEl)}
          onMouseLeave={handlePopoverClose}
        >
          <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, mb: 0.5 }}>
              Ticket IDs (Click to open):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {tickets && tickets.length > 0 ? (
                tickets.map((ticket, idx) => {
                  const ticketId = ticket.ticketId || ticket._id;
                  if (!ticketId) return null;
                  return (
                    <Box
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTicketClickInCell(ticket);
                      }}
                      sx={{
                        fontSize: "11px",
                        padding: "4px 8px",
                        background: "#00675d",
                        color: "#fff",
                        borderRadius: "4px",
                        cursor: "pointer",
                        "&:hover": {
                          background: "#005248",
                          textDecoration: "underline"
                        }
                      }}
                    >
                      {ticketId}
                    </Box>
                  );
                })
              ) : (
                <Typography sx={{ fontSize: "11px" }}>No tickets</Typography>
              )}
            </Box>
          </Box>
        </Popover>
      </td>
    );
  };
  if (loading) {
    return (
      <Paper elevation={0} sx={{ width: "100%", overflow: "hidden", borderRadius: 2, border: "1px solid", borderColor: "divider" }} className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
        <Box sx={{ background: "linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2 }}>
          <Skeleton variant="text" width={220} height={28} />
        </Box>
        <TableContainer>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb" }}>
                {[...Array(6)].map((_, i) => (
                  <th key={i} style={{ padding: "8px 12px", borderRight: i < 5 ? "1px solid #e5e7eb" : "none" }}>
                    <Skeleton variant="text" width={100} height={16} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, r) => (
                <tr key={r}>
                  {[...Array(6)].map((_, c) => (
                    <td key={c} style={{ padding: "12px", borderRight: c < 5 ? "1px solid #f3f4f6" : "none", borderTop: "1px solid #e5e7eb" }}>
                      <Skeleton variant="text" width={c < 2 ? 160 : 40} height={16} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableContainer>
      </Paper>
    );
  }
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: "100%", 
        overflow: "hidden", 
        borderRadius: 2, 
        border: "1px solid", 
        borderColor: "divider",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          borderColor: "#00675d",
        },
      }} 
      className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]"
    >
      <Box sx={{ background: "linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2 }}>
        <Typography variant="h6" sx={{fontFamily: "Inter", fontSize: "1.125rem", fontWeight: 600, color: "grey.800" }}>
          Ticket Channel Report
        </Typography>
      </Box>
      <TableContainer>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <th style={{ borderRight: "1px solid #e5e7eb", padding: "8px 12px", textAlign: "left" }}>
                <span style={{fontFamily: "Inter", color: "#374151", fontWeight: 600, letterSpacing: "0.6px", fontSize: "0.75rem" }}>ASSIGNED TO</span>
              </th>
              <th style={{ borderRight: "1px solid #e5e7eb", padding: "8px 12px", textAlign: "left" }}>
                <span style={{fontFamily: "Inter", color: "#374151", fontWeight: 600, letterSpacing: "0.6px", fontSize: "0.75rem" }}>POSITION</span>
              </th>
              <th colSpan={2} style={{ borderRight: "1px solid #e5e7eb", padding: "8px 12px", textAlign: "center" }}>
                <span style={{fontFamily: "Inter", color: "#374151", fontWeight: 600, letterSpacing: "0.6px", fontSize: "0.75rem" }}>OPEN</span>
              </th>
              <th colSpan={2} style={{ padding: "8px 12px", textAlign: "center" }}>
                <span style={{fontFamily: "Inter", color: "#374151", fontWeight: 600, letterSpacing: "0.6px", fontSize: "0.75rem" }}>CLOSED</span>
              </th>
            </tr>
            <tr style={{ backgroundColor: "#f3f4f6" }}>
              <th style={{ borderRight: "1px solid #e5e7eb", padding: 6 }} />
              <th style={{ borderRight: "1px solid #e5e7eb", padding: 6 }} />
              <th 
                onClick={() => handleSort("openWithin")} 
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                style={{ cursor: "pointer", borderRight: "1px solid #e5e7eb", padding: 6, textAlign: "center", transition: "background-color 0.2s" }}
              >
                <span style={{fontFamily: "Inter", color: "#4b5563", fontSize: "0.75rem", fontWeight: sortKey === "openWithin" ? 700 : 400 }}>
                  Within{sortKey === "openWithin" ? (sortAsc ? " ▲" : " ▼") : ""}
                </span>
              </th>
              <th 
                onClick={() => handleSort("openWithout")} 
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                style={{ cursor: "pointer", borderRight: "1px solid #e5e7eb", padding: 6, textAlign: "center", transition: "background-color 0.2s" }}
              >
                <span style={{fontFamily: "Inter", color: "#4b5563", fontSize: "0.75rem", fontWeight: sortKey === "openWithout" ? 700 : 400 }}>
                  Overdue KPI{sortKey === "openWithout" ? (sortAsc ? " ▲" : " ▼") : ""}
                </span>
              </th>
              <th 
                onClick={() => handleSort("closedWithin")} 
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                style={{ cursor: "pointer", borderRight: "1px solid #e5e7eb", padding: 6, textAlign: "center", transition: "background-color 0.2s" }}
              >
                <span style={{fontFamily: "Inter", color: "#4b5563", fontSize: "0.75rem", fontWeight: sortKey === "closedWithin" ? 700 : 400 }}>
                  Within{sortKey === "closedWithin" ? (sortAsc ? " ▲" : " ▼") : ""}
                </span>
              </th>
              <th 
                onClick={() => handleSort("closedWithout")} 
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                style={{ cursor: "pointer", padding: 6, textAlign: "center", transition: "background-color 0.2s" }}
              >
                <span style={{fontFamily: "Inter", color: "#4b5563", fontSize: "0.75rem", fontWeight: sortKey === "closedWithout" ? 700 : 400 }}>
                  Overdue KPI{sortKey === "closedWithout" ? (sortAsc ? " ▲" : " ▼") : ""}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr 
                key={index} 
                style={{ 
                  borderBottom: index === ticketChannelData.length - 1 ? "none" : "1px solid #e5e7eb",
                  transition: "background-color 0.2s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#fff";
                }}
              >
                <td style={{ borderRight: "1px solid #f3f4f6", padding: "10px 12px" }}>
                  <span style={{fontFamily: "Inter", color: "#111827", fontSize: "0.875rem" }}>{row.assignedTo}</span>
                </td>
                <td style={{ borderRight: "1px solid #f3f4f6", padding: "10px 12px" }}>
                  <span style={{fontFamily: "Inter", fontSize: "0.75rem", padding: "2px 8px", borderRadius: 16, background: row.position === "N/A" ? "#f3f4f6" : "#dbeafe", color: row.position === "N/A" ? "#374151" : "#1e40af" }}>
                    {row.position}
                  </span>
                </td>
                <TicketCountCell 
                  count={row.open?.within || 0} 
                  tickets={row.open?.withinTicketIds || []} 
                  type="open"
                />
                <TicketCountCell 
                  count={row.open?.without || 0} 
                  tickets={row.open?.withoutTicketIds || []} 
                  type="open"
                />
                <TicketCountCell 
                  count={row.closed?.within || 0} 
                  tickets={row.closed?.withinTicketIds || []} 
                  type="closed"
                />
                <TicketCountCell 
                  count={row.closed?.without || 0} 
                  tickets={row.closed?.withoutTicketIds || []} 
                  type="closed"
                  isLast={true}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </TableContainer>
    </Paper>
  );
};

export default TicketChannelTable;


